import {
  useEffect,
  useRef,
  type MouseEvent,
  type MutableRefObject
} from 'react';
import type { useWorkspaceDragPreview } from './use-workspace-drag-preview';
import type { WorkspaceRow } from './workspace';
import { getDraggedRows, WORKSPACE_DRAG_TYPE } from './workspace-dnd-rules';

// Some browsers reserve Shift + pointer movement for text selection and never
// start HTML5 dragging. Dispatch the same drag events for that gesture only.
export function useWorkspaceShiftDrag(
  draggedRows: MutableRefObject<WorkspaceRow[]>,
  reset: () => void,
  preview: Pick<ReturnType<typeof useWorkspaceDragPreview>, 'start' | 'move'>
) {
  const cancel = useRef<(() => void) | undefined>();
  const clearClick = useRef<(() => void) | undefined>();
  useEffect(
    () => () => {
      cancel.current?.();
      clearClick.current?.();
    },
    []
  );

  const onMouseDown = (
    event: MouseEvent<HTMLTableRowElement>,
    row: WorkspaceRow,
    selection: WorkspaceRow[]
  ) => {
    if (
      !event.shiftKey ||
      event.button !== 0 ||
      typeof DataTransfer === 'undefined' ||
      typeof DragEvent === 'undefined' ||
      (event.target as Element).closest(
        'button, input, select, textarea, a, [role="button"], [data-row-select]'
      )
    )
      return;
    cancel.current?.();
    const start = { x: event.clientX, y: event.clientY };
    const transfer = new DataTransfer();
    transfer.setData(WORKSPACE_DRAG_TYPE, 'move');
    transfer.effectAllowed = 'move';
    draggedRows.current = getDraggedRows(row, selection);
    let dragging = false;
    let target: Element | null = null;

    const dispatch = (
      node: Element,
      type: string,
      pointer: globalThis.MouseEvent
    ) => {
      node.dispatchEvent(
        new DragEvent(type, {
          bubbles: true,
          cancelable: true,
          dataTransfer: transfer,
          clientX: pointer.clientX,
          clientY: pointer.clientY,
          shiftKey: pointer.shiftKey,
          relatedTarget: null
        })
      );
    };
    const move = (pointer: globalThis.MouseEvent) => {
      if (!pointer.buttons) {
        stop();
        return;
      }
      if (
        !dragging &&
        Math.hypot(pointer.clientX - start.x, pointer.clientY - start.y) < 6
      )
        return;
      if (!dragging) preview.start(draggedRows.current, pointer);
      dragging = true;
      preview.move(pointer);
      const next =
        document
          .elementFromPoint(pointer.clientX, pointer.clientY)
          ?.closest('[data-workspace-drop-target]') ?? null;
      if (target && target !== next) dispatch(target, 'dragleave', pointer);
      target = next;
      if (target) dispatch(target, 'dragover', pointer);
      pointer.preventDefault();
    };
    const stop = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('blur', stop);
      window.removeEventListener('keydown', keyDown);
      cancel.current = undefined;
      reset();
    };
    const up = (pointer: globalThis.MouseEvent) => {
      if (dragging) {
        clearClick.current?.();
        const destination = document
          .elementFromPoint(pointer.clientX, pointer.clientY)
          ?.closest('[data-workspace-drop-target]');
        if (destination) dispatch(destination, 'drop', pointer);
        // A pointer fallback can generate a click after mouseup, unlike a
        // native drag. Consume that click so it cannot open/select the target.
        const swallowClick = (click: globalThis.MouseEvent) => {
          click.preventDefault();
          click.stopImmediatePropagation();
          clearClick.current?.();
        };
        window.addEventListener('click', swallowClick, {
          capture: true,
          once: true
        });
        const timer = setTimeout(() => clearClick.current?.(), 0);
        clearClick.current = () => {
          window.removeEventListener('click', swallowClick, true);
          clearTimeout(timer);
          clearClick.current = undefined;
        };
      }
      stop();
    };
    const keyDown = (key: KeyboardEvent) => {
      if (key.key === 'Escape') stop();
    };
    cancel.current = stop;
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('blur', stop);
    window.addEventListener('keydown', keyDown);
  };

  return { onMouseDown, cancel: () => cancel.current?.() };
}
